import {
  Button,
  ButtonProps,
  SizableText,
  SmartLink,
  XStack,
  YStack,
  YStackProps,
  isWeb,
  useToastController,
} from '@my/ui'
import { Check, Settings, Share2, Users2 } from '@tamagui/lucide-icons'
import config from 'app/config'
import { ShareModal } from 'app/features/sharing/components'
import { useAuth } from 'app/provider/auth'
import { useJoinGroup } from './commands'
import { GroupCardOptions } from './group-card-options'
import { fetchSuggestedGroupsQuery } from '../../screens/groups-screen/queries'
import { userGroupJoinRequestsQuery, userGroupsQuery } from '../../queries'
import { GroupVisibility } from 'app/types/GroupVisibility'

export type GroupCardControlsProps = {
  id: string
  visibility: GroupVisibility
  href: string
  canJoin?: boolean
  isAdmin?: boolean
  hasPendingRequest?: boolean
  hasJoined?: boolean
  canLeave?: boolean
  shareable?: boolean
  joinButtonProps?: ButtonProps
}

export const GroupCardControls = ({
  id,
  href,
  visibility,
  shareable,
  hasJoined,
  canJoin,
  canLeave,
  hasPendingRequest,
  isAdmin,
  joinButtonProps,
  ...props
}: GroupCardControlsProps & YStackProps) => {
  const toast = useToastController()
  const { user } = useAuth()
  const [joinGroup, { loading }] = useJoinGroup()

  const handleJoinGroup = async () => {
    await joinGroup({
      variables: {
        groupId: id,
      },
      onCompleted: () => {
        toast.show(
          visibility === 'private' ? `Requested to join the group` : `You've joined the group`
        )
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: userGroupJoinRequestsQuery.query,
          variables: {
            ...userGroupJoinRequestsQuery.defaultVariables,
          },
        },
        {
          query: userGroupsQuery.query,
          variables: {
            ...userGroupsQuery.defaultVariables,
          },
        },
        {
          query: fetchSuggestedGroupsQuery,
          variables: {
            limit: 10,
          },
        },
      ],
      onError: (error) => {
        toast.show(error.message || 'Could not join the group', {
          burntOptions: {
            preset: 'error',
          },
        })
      },
    })
  }

  return (
    <YStack gap="$2" jc="space-between" {...props}>
      {isWeb && (
        <GroupCardOptions
          groupId={id}
          canLeave={!!canLeave}
          href={href}
          user={user}
        />
      )}

      {shareable && (
        <ShareModal
          href={config.siteUrl + href}
          subject="Check out this group!"
          trigger={<Button size="$3" px="$2" plain icon={<Share2 size={24} />} />}
        />
      )}

      {isAdmin && (
        <SmartLink
          Component={Button}
          theme="primary"
          aria-label="Manage group"
          href={`/groups/${id}/admin`}
          size="$3"
          mt="auto"
          icon={<Settings size={18} />}
          {...joinButtonProps}
        >
          <SizableText size="$4" fow="600">
            Manage
          </SizableText>
        </SmartLink>
      )}

      {canJoin && !isAdmin && (
        <Button
          theme="primary"
          aria-label="Join group"
          onPress={handleJoinGroup}
          loading={loading}
          disabled={hasJoined || hasPendingRequest}
          opacity={hasJoined || hasPendingRequest ? 0.5 : 1}
          icon={hasJoined ? <Check size={18} /> : <Users2 size={18} />}
          size="$3"
          mt="auto"
          {...joinButtonProps}
        >
          <SizableText size="$4" fow="600">
            {hasJoined && 'Joined'}
            {!hasJoined && !hasPendingRequest && 'Join'}
            {hasPendingRequest && 'Pending...'}
          </SizableText>
        </Button>
      )}
    </YStack>
  )
}
